// Cognito OIDC (Authorization Code + PKCE) configuration for Orenda.
//
// Postman equivalent:
//   Auth URL:         https://bopayments.auth.eu-west-1.amazoncognito.com/oauth2/authorize
//   Access Token URL: https://bopayments.auth.eu-west-1.amazoncognito.com/oauth2/token
//   Grant type:       Authorization Code (With PKCE)
//   Callback URL:     <scheme>://<host>/orenda/login
//   Client ID:        <the same client id you use in Postman>
//   Client Secret:    (empty for public/SPA clients)
//
// The `authority` here is the Cognito user-pool issuer URL. oidc-client-ts
// discovers /authorize, /token, /userinfo, JWKS, etc. from
// `${authority}/.well-known/openid-configuration`.
//
// IMPORTANT: the Cognito App Client in the AWS Console must:
//   - Have "Authorization code grant" enabled
//   - NOT have a client secret (public client / SPA)
//   - Have EACH environment's redirect_uri registered as a Callback URL:
//       http://localhost:3003/orenda/login       (local dev)
//       https://dev.card-console.com/orenda/login
//       https://staging.card-console.com/orenda/login
//       https://card-console.com/orenda/login    (prod)
//     plus the matching /orenda/logout entries in the Sign-out URL list.

// Compute origin at module-load so the SAME build (dev / staging / prod)
// sends the right callback for whichever host it's served from. Without
// this, hardcoding one URL broke every other environment because Cognito
// would fall back to the app client's default and land users on `/`.
const ORIGIN =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : "http://localhost:3003";

export const orendaOidcConfig = {
  // Cognito user-pool issuer (matches the `iss` claim in issued tokens).
  // Format: https://cognito-idp.<region>.amazonaws.com/<userPoolId>
  authority: "https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_7hNMPxwoN",

  // Same client id you paste into Postman's "Client ID" field.
  client_id: "5g5vc7781tneu4uj3cuho2631i",

  // Where Cognito will send the auth code back to. Must be registered as a
  // Callback URL on the Cognito App Client, exact match for the current host.
  redirect_uri: `${ORIGIN}/orenda/login`,

  // Where Cognito redirects after logout. Must be in Cognito's Sign-out URL list.
  post_logout_redirect_uri: `${ORIGIN}/orenda/logout`,

  // Federated identity provider inside Cognito that we want to delegate to.
  // With this passed on /oauth2/authorize, Cognito skips its own login page
  // and jumps straight to Microsoft Entra. Confirmed by Orenda (Christian).
  identity_provider: "bopayments-entra",

  // App Client only has `openid` and `email` enabled — requesting `profile`
  // triggers Cognito's invalid_scope error on /oauth2/authorize.
  scope: "openid email",

  // Code + PKCE (Cognito supports this for public SPA clients).
  response_type: "code",

  // Where MSAL used sessionStorage; keep parity.
  // (oidc-client-ts default is sessionStorage, so this is documentation only.)
  automaticSilentRenew: true,

  // Cognito's logout endpoint uses a slightly different URL layout — set this
  // so signoutRedirect() hits the right endpoint.
  metadataUrl:
    "https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_7hNMPxwoN/.well-known/openid-configuration",
};
